import { FormattedMessage } from "react-intl";
import moment from "moment";

import Badge, { BadgeColor } from "components/v2/Badge/Badge";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import { ReactComponent as ArrowSquareUpIcon } from "assets/images/iconsV2/arrow-square-up-right.svg";
import testIds from "constants/testIds";
import TopNavLocationDetail from "./TopNavLocationDetail";
import TopNavCreatedBy from "./TopNavCreatedBy";

const quickStatusColorMap: Record<string, BadgeColor> = {
  Shipping: "brand",
  Processing: "warning",
  Pending: "warning",
  Draft: "gray",
  Archived: "gray",
  Canceled: "gray",
};

interface TopNavQuickRequestDetailsProps {
  data: any;
}

function TopNavQuickRequestDetails({ data }: TopNavQuickRequestDetailsProps) {
  const schedule = data?.schedule || {};
  const { address_v2 } = schedule;
  const hasAddressV2 = Object.keys(address_v2 ?? {}).some(
    (key) => key !== "pickup_is_port_address" && key !== "delivery_is_port_address",
  );

  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data, useRevampedDesign: true });

  const {
    quick_request_creation_date,
    quick_request_creation_time,
    quick_request_status,
    quick_request_created_by_name,
    quick_request_created_by_email,
  } = data?.organization || {};

  const { related_request_id, related_request_status } = data?.related_request || {};

  const shipmentRedirectURL = ["Booked", "In Transit", "Delayed", "Delivered", "Incomplete"].includes(related_request_status)
    ? `/shipment/${related_request_id}/tracking`
    : `/shipment/${related_request_id}`;

  const hasDetails = schedule.pickup_address_name || schedule.stop1_address_name || quick_request_created_by_name;

  const dateTime = `${quick_request_creation_date} ${quick_request_creation_time}`;
  const formattedDateTime = moment(dateTime, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm");

  if (!hasDetails) return null;

  return (
    <div className="topnav-v2__details" data-testid={testIds.TOPNAV_V2_QUICK_REQUEST_DETAILS}>
      {/* Status & Date created */}
      <div className="topnav-v2__timers" data-testid={testIds.TOPNAV_V2_QUICK_REQUEST_STATUS_CONTAINER}>
        {quick_request_status && (
          <div data-testid={testIds.TOPNAV_V2_QUICK_REQUEST_STATUS_ITEM}>
            <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_QUICK_REQUEST_STATUS_LABEL}>
              <FormattedMessage id="general.status" defaultMessage="Status" />
            </span>
            <div className="w-fit" data-testid={testIds.TOPNAV_V2_QUICK_REQUEST_STATUS_VALUE}>
              <Badge
                color={quickStatusColorMap[quick_request_status] ?? "gray"}
                type="status-badge"
                size="sm"
                label={quick_request_status}
              />
            </div>
          </div>
        )}
      </div>

      {/* Pickup */}
      {schedule.pickup_address_name && (
        <TopNavLocationDetail
          type="pickup"
          addressName={schedule.pickup_address_name}
          addressFields={[
            schedule.pickup_state_code,
            schedule.pickup_zipcode,
            schedule.pickup_city,
            schedule.pickup_country_code,
          ]}
          copyFields={[
            schedule.pickup_address_name,
            schedule.pickup_zipcode,
            schedule.pickup_city,
            schedule.pickup_country_code,
          ]}
          date={schedule.pickup_date}
          time={schedule.pickup_time}
          endDate={schedule.pickup_end_date}
          endTime={schedule.pickup_end_time}
          hasAddressV2={hasAddressV2}
          renderAddressV2={renderPickupAddress}
          automationIdPrefix="topnav-v2-quick-request-pickup"
        />
      )}

      {/* Delivery */}
      {schedule.stop1_address_name && (
        <TopNavLocationDetail
          type="delivery"
          addressName={schedule.stop1_address_name}
          addressFields={[
            schedule.stop1_state_code,
            schedule.stop1_zipcode,
            schedule.stop1_city,
            schedule.stop1_country_code,
          ]}
          copyFields={[
            schedule.stop1_address_name,
            schedule.stop1_zipcode,
            schedule.stop1_city,
            schedule.stop1_country_code,
          ]}
          date={schedule.stop1_date}
          time={schedule.stop1_time}
          endDate={schedule.stop1_end_date}
          endTime={schedule.stop1_end_time}
          isAsap={schedule.stop1_datetime === "ASAP"}
          hasAddressV2={hasAddressV2}
          renderAddressV2={renderDeliveryAddress}
          automationIdPrefix="topnav-v2-quick-request-delivery"
        />
      )}

      {/* Created by */}
      {quick_request_created_by_name && (
        <TopNavCreatedBy
          name={quick_request_created_by_name}
          email={quick_request_created_by_email}
          subtitle={formattedDateTime}
          link={
            related_request_id
              ? {
                  to: shipmentRedirectURL,
                  label: related_request_id,
                  icon: <ArrowSquareUpIcon height={16} width={16} />,
                }
              : undefined
          }
          automationIdPrefix="topnav-v2-quick-request-created-by"
        />
      )}
    </div>
  );
}

export default TopNavQuickRequestDetails;
